import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser, useLogin, useRegister } from '../hooks/api';
import type { User, LoginRequest, RegisterRequest } from '../types/api.types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => void;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('access_token'));

  // Fetch current user if we have a token
  const { data: user, isLoading: isUserLoading, refetch } = useCurrentUser();

  const loginMutation = useLogin();
  const registerMutation = useRegister();

  // Sync token to localStorage
  useEffect(() => {
    if (token) {
      localStorage.setItem('access_token', token);
    } else {
      localStorage.removeItem('access_token');
    }
  }, [token]);

  const login = async (credentials: LoginRequest) => {
    try {
      const response = await loginMutation.mutateAsync(credentials);
      setToken(response.access_token);
      // Refetch user data after login
      await refetch();
      navigate('/dashboard');
    } catch (error) {
      // Error is handled by mutation error state
      throw error;
    }
  };

  const register = async (data: RegisterRequest) => {
    try {
      const response = await registerMutation.mutateAsync(data);
      // Registration now returns a message, not a token
      // Navigate to login page with success message
      navigate('/login?registered=true&message=' + encodeURIComponent(response.message));
    } catch (error) {
      // Error is handled by mutation error state
      throw error;
    }
  };

  const logout = () => {
    setToken(null);
    localStorage.removeItem('access_token');
    navigate('/login');
  };

  const isAuthenticated = !!token && !!user;
  const isAdmin = user?.is_admin ?? false;
  const isLoading = !!token && isUserLoading;

  const value: AuthContextType = {
    user: user || null,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
